import { useState, useRef, useCallback } from "react";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import {
  Grid3x3,
  MousePointer2,
  Pencil,
  Highlighter,
  Eraser,
  Undo2,
  Redo2,
  Trash2,
  ChevronUp,
  Type,
  Sticker,
  Target,
  Minus,
  MoveUpRight,
  Square,
  Circle,
  Check,
  X,
  Star,
  Heart,
  CircleHelp,
} from "lucide-react";
import { ConfirmDialog } from "@vc/views/dialogs/ConfirmDialog";
import type { AnnotationTool, ShapeTool, StampKind } from "@/features/meeting/services/annotation-geometry";

/** The palette. Labels reuse the old whiteboard's translated colour names. */
export const ANNOTATION_COLORS = [
  { hex: "#ef4444", key: "panels.colorRed" },
  { hex: "#FF8C00", key: "panels.colorOrange" },
  { hex: "#FFD700", key: "panels.colorGold" },
  { hex: "#22c55e", key: "panels.colorGreen" },
  { hex: "#3b82f6", key: "panels.colorBlue" },
  { hex: "#9333ea", key: "panels.colorPurple" },
  { hex: "#FF5F87", key: "panels.colorRose" },
  { hex: "#111111", key: "panels.colorBlack" },
] as const;

const SHAPES: { id: ShapeTool; icon: typeof Pencil; key: string }[] = [
  { id: "line", icon: Minus, key: "panels.line" },
  { id: "arrow", icon: MoveUpRight, key: "panels.arrow" },
  { id: "rect", icon: Square, key: "panels.rectangle" },
  { id: "ellipse", icon: Circle, key: "panels.circle" },
];

const STAMPS: { id: StampKind; icon: typeof Pencil; key: string }[] = [
  { id: "check", icon: Check, key: "panels.check" },
  { id: "cross", icon: X, key: "panels.cross" },
  { id: "star", icon: Star, key: "panels.star" },
  { id: "heart", icon: Heart, key: "panels.heart" },
  { id: "question", icon: CircleHelp, key: "panels.question" },
];

interface AnnotationToolbarProps {
  tool: AnnotationTool;
  onToolChange: (tool: AnnotationTool) => void;
  /** The shape the shape button draws, remembered between uses. */
  shape: ShapeTool;
  onShapeChange: (shape: ShapeTool) => void;
  /** The stamp the stamp button places. */
  stamp: StampKind;
  onStampChange: (stamp: StampKind) => void;
  color: string;
  onColorChange: (hex: string) => void;
  onUndo: () => void;
  onRedo: () => void;
  /** Clears your own drawings on this document. */
  onClear: () => void;
  /** Clears everyone's drawings — offered only when `canClearAll`. */
  onClearAll: () => void;
  canUndo: boolean;
  canRedo: boolean;
  canClear: boolean;
  /** Host or co-host: may clear everyone's drawings. */
  canClearAll: boolean;
}

type Menu = "shape" | "stamp" | "color" | "clear" | null;

/**
 * The annotation tools over a presented document: a vertical panel down the
 * side of the stage, draggable and collapsible. Holds no drawing state of its
 * own — the document layout owns the tool, shape, stamp and colour, and the
 * marks live in the annotation store.
 *
 * Shapes, stamps, colour and (for a host) clear each open a small menu beside
 * the panel; the panel's own button remembers the last choice, so the common
 * case is one click.
 */
export function AnnotationToolbar({
  tool,
  onToolChange,
  shape,
  onShapeChange,
  stamp,
  onStampChange,
  color,
  onColorChange,
  onUndo,
  onRedo,
  onClear,
  onClearAll,
  canUndo,
  canRedo,
  canClear,
  canClearAll,
}: AnnotationToolbarProps) {
  const t = useTranslation();
  const [position, setPosition] = useState(() => {
    const centeredY = typeof window !== 'undefined' ? window.innerHeight * 0.02 : 100;
    return { x: 10, y: centeredY };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [menu, setMenu] = useState<Menu>(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-tool]')) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  }, [position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y,
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const toggleMenu = (m: Menu) => setMenu((cur) => (cur === m ? null : m));
  const pick = (next: AnnotationTool) => {
    onToolChange(next);
    setMenu(null);
  };
  // A group button (shapes, stamps): the first click takes up its last
  // choice and shows the others; a click while it is in hand toggles them.
  const groupClick = (inHand: boolean, next: AnnotationTool, m: Menu) => {
    if (inHand) {
      toggleMenu(m);
      return;
    }
    onToolChange(next);
    setMenu(m);
  };

  const ShapeIcon = SHAPES.find((s) => s.id === shape)?.icon ?? Circle;
  const StampIcon = STAMPS.find((s) => s.id === stamp)?.icon ?? Sticker;
  const shapeActive = SHAPES.some((s) => s.id === tool);

  const button = (active: boolean) =>
    `p-2 cursor-pointer rounded-lg transition relative disabled:cursor-default disabled:opacity-40 ${active
      ? "bg-secondary-600 text-white"
      : "hover:bg-slate-100 dark:hover:bg-muted text-slate-600 dark:text-muted-foreground"
    }`;

  /** A small panel beside the bar, level with the button that opened it. */
  const flyout = "absolute left-full top-0 ml-2 rounded-xl border border-vc-panel-border bg-card/95 p-1.5 shadow-lg backdrop-blur-sm";

  if (isCollapsed) {
    return (
      <div
        className="absolute z-50 cursor-pointer"
        style={{ left: position.x, top: position.y }}
        onClick={() => setIsCollapsed(false)}
      >
        <div className="bg-card/90 backdrop-blur-sm rounded-lg shadow-lg p-2 hover:bg-card transition">
          <Grid3x3 className="h-5 w-5 text-slate-600 dark:text-muted-foreground" />
        </div>
      </div>
    );
  }

  const toolButton = (id: AnnotationTool, Icon: typeof Pencil, label: string) => (
    <button
      key={id}
      type="button"
      data-tool
      onClick={() => pick(id)}
      className={button(tool === id)}
      title={label}
      aria-label={label}
      aria-pressed={tool === id}
    >
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div
      role="toolbar"
      aria-orientation="vertical"
      aria-label={t("dialogs.annotationTools")}
      className="absolute z-50 select-none"
      style={{ left: position.x, top: position.y }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="bg-card/95 backdrop-blur-sm py-2 rounded-2xl shadow-lg border border-vc-panel-border flex flex-col items-center gap-1 min-w-[40px]">
        {/* Drag Handle */}
        <div
          className="rounded-lg cursor-grab active:cursor-grabbing hover:bg-slate-100 dark:hover:bg-muted transition"
          onMouseDown={(e) => {
            e.stopPropagation();
            handleMouseDown(e);
          }}
        >
          <Grid3x3 className="h-4 w-4 text-slate-500 dark:text-muted-foreground" />
        </div>

        <div className="w-full h-px bg-slate-200 dark:bg-border my-1" />

        {toolButton("select", MousePointer2, t("panels.select"))}
        {toolButton("pen", Pencil, t("panels.pen"))}
        {toolButton("highlighter", Highlighter, t("panels.highlighter"))}

        {/* Shapes: the button draws the last shape picked; the menu changes it. */}
        <div className="relative" data-tool>
          <button
            type="button"
            data-tool
            onClick={() => groupClick(shapeActive, shape, "shape")}
            className={button(shapeActive)}
            title={t("panels.shape")}
            aria-label={t("panels.shape")}
            aria-pressed={shapeActive}
            aria-expanded={menu === "shape"}
          >
            <ShapeIcon className="h-4 w-4" />
          </button>
          {menu === "shape" && (
            <div className={`${flyout} flex gap-1`}>
              {SHAPES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-tool
                  onClick={() => {
                    onShapeChange(s.id);
                    pick(s.id);
                  }}
                  className={button(tool === s.id)}
                  title={t(s.key)}
                  aria-label={t(s.key)}
                  aria-pressed={tool === s.id}
                >
                  <s.icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          )}
        </div>

        {toolButton("text", Type, t("panels.text"))}

        {/* Stamps: as shapes — the last one picked, one click away. */}
        <div className="relative" data-tool>
          <button
            type="button"
            data-tool
            onClick={() => groupClick(tool === "stamp", "stamp", "stamp")}
            className={button(tool === "stamp")}
            title={t("panels.stamp")}
            aria-label={t("panels.stamp")}
            aria-pressed={tool === "stamp"}
            aria-expanded={menu === "stamp"}
          >
            <StampIcon className="h-4 w-4" />
          </button>
          {menu === "stamp" && (
            <div className={`${flyout} flex gap-1`}>
              {STAMPS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  data-tool
                  onClick={() => {
                    onStampChange(s.id);
                    pick("stamp");
                  }}
                  className={button(stamp === s.id)}
                  title={t(s.key)}
                  aria-label={t(s.key)}
                  aria-pressed={stamp === s.id}
                >
                  <s.icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          )}
        </div>

        {toolButton("laser", Target, t("panels.laserPointer"))}
        {toolButton("eraser", Eraser, t("panels.eraser"))}

        {/* Colour: the swatch shows the current one; the palette opens beside the bar. */}
        <div className="relative" data-tool>
          <button
            type="button"
            data-tool
            onClick={() => toggleMenu("color")}
            className={button(false)}
            title={t("panels.color")}
            aria-label={t("panels.color")}
            aria-expanded={menu === "color"}
          >
            <span className="block h-4 w-4 rounded-full ring-1 ring-black/20" style={{ backgroundColor: color }} />
          </button>
          {/* Columns sized by the swatches, not `grid-cols-4`: that is
              minmax(0, 1fr), and a menu placed beside a 32px button sizes to
              its min-content, so all eight swatches piled into one spot. */}
          {menu === "color" && (
            <div className={`${flyout} grid grid-cols-[repeat(4,auto)] gap-1.5 p-2`}>
              {ANNOTATION_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  data-tool
                  onClick={() => {
                    onColorChange(c.hex);
                    setMenu(null);
                    // Choosing a colour is choosing to mark — unless a marking tool is already in hand.
                    if (tool === "select" || tool === "eraser") onToolChange("pen");
                  }}
                  className={`h-6 w-6 cursor-pointer rounded-full ring-offset-1 transition ${color === c.hex ? "ring-2 ring-secondary-600" : "ring-1 ring-black/20 hover:scale-110"}`}
                  style={{ backgroundColor: c.hex }}
                  title={t(c.key)}
                  aria-label={t(c.key)}
                  aria-pressed={color === c.hex}
                />
              ))}
            </div>
          )}
        </div>

        <div className="w-full h-px bg-slate-200 dark:bg-border my-1" />

        <button type="button" data-tool onClick={onUndo} disabled={!canUndo} className={button(false)} title={t("panels.undo")} aria-label={t("panels.undo")}>
          <Undo2 className="h-4 w-4" />
        </button>
        <button type="button" data-tool onClick={onRedo} disabled={!canRedo} className={button(false)} title={t("panels.redo")} aria-label={t("panels.redo")}>
          <Redo2 className="h-4 w-4" />
        </button>

        {/* Clear: your own drawings; a host or co-host chooses between theirs
            and everyone's, and is asked before wiping other people's work. */}
        <div className="relative" data-tool>
          <button
            type="button"
            data-tool
            onClick={() => {
              if (canClearAll) toggleMenu("clear");
              else onClear();
            }}
            disabled={!canClearAll && !canClear}
            className={button(menu === "clear")}
            title={t("panels.clear")}
            aria-label={t("panels.clear")}
            aria-expanded={canClearAll ? menu === "clear" : undefined}
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {menu === "clear" && (
            <div className={`${flyout} flex w-max flex-col gap-0.5 text-sm`}>
              <button
                type="button"
                data-tool
                disabled={!canClear}
                onClick={() => {
                  onClear();
                  setMenu(null);
                }}
                className="cursor-pointer rounded-lg px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100 disabled:cursor-default disabled:opacity-40 dark:text-foreground dark:hover:bg-muted"
              >
                {t("panels.clearMyDrawings")}
              </button>
              <button
                type="button"
                data-tool
                onClick={() => {
                  setMenu(null);
                  setConfirmClearAll(true);
                }}
                className="cursor-pointer rounded-lg px-3 py-1.5 text-left text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
              >
                {t("panels.clearAllDrawings")}
              </button>
            </div>
          )}
        </div>

        <div className="w-full h-px bg-slate-200 dark:bg-border my-1" />
        <button
          type="button"
          onClick={() => setIsCollapsed(true)}
          className="cursor-pointer rounded-lg hover:bg-slate-100 dark:hover:bg-muted text-slate-600 dark:text-muted-foreground transition"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>

      <ConfirmDialog
        open={confirmClearAll}
        title={t("panels.clearAllDrawings")}
        description={t("panels.clearAllDrawingsConfirm")}
        destructive
        onConfirm={() => {
          setConfirmClearAll(false);
          onClearAll();
        }}
        onCancel={() => setConfirmClearAll(false)}
      />
    </div>
  );
}
